import type { SentimentBucket } from "@/config/fng";

interface MessageSet {
  [locale: string]: string[];
}

const messages: Record<SentimentBucket, MessageSet> = {
  "Extreme Fear": {
    en: [
      "Cold blood. Clear eyes.",
      "Panic is loud. Opportunity is quiet.",
      "Red screens. Steady hands.",
      "Fear is everywhere. Discipline is rare.",
      "Blood in the streets. Are you watching?",
      "The crowd is running. Where are you?",
      "Maximum pessimism. Maximum attention.",
      "Capitulation whispers louder than charts.",
      "When nobody wants it, somebody should.",
      "Dark candles. Bright minds.",
      "The market is scared. Are you?",
      "Volatility is the price of returns.",
      "Pain now. Stories later.",
      "Extreme fear rarely lasts forever.",
      "Sentiment is a lagging indicator of courage.",
      "Headlines sell fear. Charts tell truth.",
      "The bottom is a process, not a moment.",
      "Risk feels unbearable at the best entries.",
      "History rewards the uncomfortable.",
      "Everyone is certain it's over. Again.",
      "The exit is crowded. The entrance is empty.",
      "Markets heal in silence.",
      "Fear makes everything feel permanent.",
      "This is where conviction is tested.",
      "The loudest voices are the most afraid.",
      // Phase 4 additions
      "Despair is a sentiment, not a forecast.",
      "Zoom out. Breathe in.",
      "The chart doesn't care about your feelings.",
      "Sellers exhausted. Buyers invisible.",
      "Gravity wins until it doesn't.",
      "Survival mode. Good sign.",
      "The market is pricing in the end of the world. Again.",
      "Liquidity hides in the darkest corners.",
      "Strong hands are forged in red.",
      "This too is data.",
    ],
    es: [
      "Sangre fría. Ojos claros.",
      "El pánico es ruidoso. La oportunidad es silenciosa.",
      "Pantallas rojas. Manos firmes.",
      "El miedo está en todas partes. La disciplina es rara.",
      "Sangre en las calles. ¿Estás mirando?",
      "La multitud corre. ¿Dónde estás tú?",
      "Máximo pesimismo. Máxima atención.",
      "La capitulación susurra más fuerte que los gráficos.",
      "Cuando nadie lo quiere, alguien debería.",
      "Velas oscuras. Mentes brillantes.",
      "El mercado tiene miedo. ¿Y tú?",
      "La volatilidad es el precio de los retornos.",
      "Dolor ahora. Historias después.",
      "El miedo extremo rara vez dura para siempre.",
      "El sentimiento va detrás del coraje.",
      "Los titulares venden miedo. Los gráficos dicen la verdad.",
      "El fondo es un proceso, no un momento.",
      "El riesgo parece insoportable en las mejores entradas.",
      "La historia premia a los incómodos.",
      "Todos están seguros de que se acabó. Otra vez.",
      "La salida está llena. La entrada está vacía.",
      "Los mercados sanan en silencio.",
      "El miedo hace que todo parezca permanente.",
      "Aquí es donde se prueba la convicción.",
      "Las voces más fuertes son las más asustadas.",
      // Phase 4 additions
      "La desesperación es un sentimiento, no un pronóstico.",
      "Aleja el zoom. Respira.",
      "Al gráfico no le importan tus sentimientos.",
      "Vendedores agotados. Compradores invisibles.",
      "La gravedad gana hasta que deja de ganar.",
      "Modo supervivencia. Buena señal.",
      "El mercado está valorando el fin del mundo. Otra vez.",
      "La liquidez se esconde en los rincones más oscuros.",
      "Las manos fuertes se forjan en rojo.",
      "Esto también son datos.",
    ],
    fr: [
      "Sang froid. Regard clair.",
      "La panique est bruyante. L'opportunité est silencieuse.",
      "Écrans rouges. Mains fermes.",
      "La peur est partout. La discipline est rare.",
      "Le marché a peur. Et toi ?",
      "La volatilité est le prix des rendements.",
      "Le fond est un processus, pas un moment.",
      "L'histoire récompense ceux qui sont mal à l'aise.",
    ],
    de: [
      "Kaltes Blut. Klarer Blick.",
      "Panik ist laut. Gelegenheit ist leise.",
      "Rote Bildschirme. Ruhige Hände.",
      "Angst ist überall. Disziplin ist selten.",
      "Der Markt hat Angst. Und du?",
      "Volatilität ist der Preis der Rendite.",
      "Der Boden ist ein Prozess, kein Moment.",
      "Die Geschichte belohnt die Unbequemen.",
    ],
    it: [
      "Sangue freddo. Occhi chiari.",
      "Il panico è rumoroso. L'opportunità è silenziosa.",
      "Schermi rossi. Mani ferme.",
      "La paura è ovunque. La disciplina è rara.",
      "Il mercato ha paura. E tu?",
      "La volatilità è il prezzo dei rendimenti.",
      "Il fondo è un processo, non un momento.",
      "La storia premia chi è scomodo.",
    ],
    pt: [
      "Sangue frio. Olhos claros.",
      "O pânico é barulhento. A oportunidade é silenciosa.",
      "Telas vermelhas. Mãos firmes.",
      "O medo está em toda parte. A disciplina é rara.",
      "O mercado está com medo. E você?",
      "Volatilidade é o preço dos retornos.",
      "O fundo é um processo, não um momento.",
      "A história recompensa os desconfortáveis.",
    ],
  },
  "Fear": {
    en: [
      "Caution is in the air.",
      "The crowd is hesitating.",
      "Tension rising. Stay selective.",
      "Risk feels expensive today.",
      "Uncertainty breeds opportunity.",
      "The herd is nervous. Watch the edges.",
      "Fear is a compass, not a cage.",
      "Conviction is cheap when it's easy.",
      "Whispers of doubt. Worth listening.",
      "Markets don't reward comfort.",
      "Position size matters more than direction.",
      "Patience looks like inaction. It isn't.",
      "The bid is thin. The ask is loud.",
      "Sentiment shifts before price does.",
      "Not panicking yet. Just uneasy.",
      "The crowd sees risk. Do you see value?",
      "Caution and conviction can coexist.",
      "Nervousness is not the same as wrong.",
      "The smart money is quiet right now.",
      "Fear creates the discounts nobody wants.",
      "Hesitation is the market thinking.",
      "Downside feels real. Upside feels imaginary.",
      "This is where plans get tested.",
      "Emotional markets. Rational decisions.",
      "The trend is your friend until sentiment shifts.",
      // Phase 4 additions
      "Anxiety is not analysis.",
      "The fog lifts. Eventually.",
      "Cheap conviction, expensive lessons.",
      "Sellers have stories. Buyers have spreadsheets.",
      "The market is whispering. Are you listening?",
      "Not every dip is a gift. Not every rally is real.",
      "Unease is the price of being early.",
      "Volume tells you who showed up.",
      "Doubt is free. Conviction costs something.",
      "The crowd hedges. The crowd is usually late.",
    ],
    es: [
      "La cautela está en el aire.",
      "La multitud está dudando.",
      "La tensión sube. Sé selectivo.",
      "El riesgo se siente caro hoy.",
      "La incertidumbre genera oportunidad.",
      "El rebaño está nervioso. Observa los bordes.",
      "El miedo es una brújula, no una jaula.",
      "La convicción es barata cuando es fácil.",
      "Susurros de duda. Vale la pena escuchar.",
      "Los mercados no premian la comodidad.",
      "El tamaño de la posición importa más que la dirección.",
      "La paciencia parece inacción. No lo es.",
      "La demanda es débil. La oferta es ruidosa.",
      "El sentimiento cambia antes que el precio.",
      "No hay pánico aún. Solo inquietud.",
      "La multitud ve riesgo. ¿Tú ves valor?",
      "Cautela y convicción pueden coexistir.",
      "El nerviosismo no es lo mismo que equivocarse.",
      "El dinero inteligente está callado ahora.",
      "El miedo crea los descuentos que nadie quiere.",
      "La indecisión es el mercado pensando.",
      "La baja se siente real. La subida se siente imaginaria.",
      "Aquí es donde los planes se ponen a prueba.",
      "Mercados emocionales. Decisiones racionales.",
      "La tendencia es tu amiga hasta que el sentimiento cambia.",
      // Phase 4 additions
      "La ansiedad no es análisis.",
      "La niebla se levanta. Eventualmente.",
      "Convicción barata, lecciones caras.",
      "Los vendedores tienen historias. Los compradores tienen hojas de cálculo.",
      "El mercado susurra. ¿Estás escuchando?",
      "No toda caída es un regalo. No toda subida es real.",
      "La inquietud es el precio de llegar temprano.",
      "El volumen te dice quién se presentó.",
      "La duda es gratis. La convicción cuesta.",
      "La multitud se cubre. La multitud suele llegar tarde.",
    ],
    fr: [
      "La prudence est dans l'air.",
      "La foule hésite.",
      "La tension monte. Reste sélectif.",
      "Le risque semble cher aujourd'hui.",
      "L'incertitude engendre l'opportunité.",
      "La peur est une boussole, pas une cage.",
      "La patience ressemble à l'inaction. Ce n'est pas le cas.",
      "Le sentiment change avant le prix.",
    ],
    de: [
      "Vorsicht liegt in der Luft.",
      "Die Masse zögert.",
      "Spannung steigt. Bleib selektiv.",
      "Risiko fühlt sich heute teuer an.",
      "Unsicherheit erzeugt Gelegenheit.",
      "Angst ist ein Kompass, kein Käfig.",
      "Geduld sieht aus wie Untätigkeit. Ist es aber nicht.",
      "Stimmung ändert sich vor dem Preis.",
    ],
    it: [
      "La cautela è nell'aria.",
      "La folla esita.",
      "La tensione sale. Resta selettivo.",
      "Il rischio sembra caro oggi.",
      "L'incertezza genera opportunità.",
      "La paura è una bussola, non una gabbia.",
      "La pazienza sembra inazione. Non lo è.",
      "Il sentimento cambia prima del prezzo.",
    ],
    pt: [
      "A cautela está no ar.",
      "A multidão está hesitando.",
      "Tensão subindo. Seja seletivo.",
      "O risco parece caro hoje.",
      "Incerteza gera oportunidade.",
      "O medo é uma bússola, não uma jaula.",
      "Paciência parece inação. Não é.",
      "O sentimento muda antes do preço.",
    ],
  },
  "Neutral": {
    en: [
      "No edge. Stay sharp.",
      "Balance rarely lasts.",
      "Nothing obvious. Watch closely.",
      "Flat emotion. Useful signal.",
      "The market is thinking.",
      "Equilibrium is temporary.",
      "No fear. No greed. Just data.",
      "The calm between storms.",
      "Patience is a position.",
      "Neutral doesn't mean safe.",
      "The crowd can't decide. Can you?",
      "Sideways is also a direction.",
      "Indecision is information.",
      "Wait for the shift, not the noise.",
      "The boring part matters most.",
      "No narrative dominates. Stay flexible.",
      "When in doubt, zoom out.",
      "Quiet markets hide loud moves.",
      "The setup comes before the signal.",
      "Neither fear nor greed. Just attention.",
      // Phase 4 additions
      "Coiled spring. Which way?",
      "The market holds its breath.",
      "Stillness before the storm. Or the sunshine.",
      "Range-bound minds make range-bound trades.",
      "Consensus is fragile here.",
      "The middle is the hardest place to stand.",
      "No thesis? No trade. That's fine.",
      "Boredom is underrated alpha.",
      "The chart is resting. You should too.",
      "Fifty-fifty feels honest for once.",
    ],
    es: [
      "Sin ventaja. Mantente alerta.",
      "El equilibrio rara vez dura.",
      "Nada obvio. Observa de cerca.",
      "Emoción plana. Señal útil.",
      "El mercado está pensando.",
      "El equilibrio es temporal.",
      "Sin miedo. Sin codicia. Solo datos.",
      "La calma entre tormentas.",
      "La paciencia es una posición.",
      "Neutral no significa seguro.",
      "La multitud no puede decidir. ¿Puedes tú?",
      "Lateral también es una dirección.",
      "La indecisión es información.",
      "Espera el cambio, no el ruido.",
      "La parte aburrida es la que más importa.",
      "Ninguna narrativa domina. Sé flexible.",
      "Cuando dudes, aleja el zoom.",
      "Los mercados tranquilos esconden movimientos grandes.",
      "La preparación viene antes de la señal.",
      "Ni miedo ni codicia. Solo atención.",
      // Phase 4 additions
      "Resorte comprimido. ¿Hacia dónde?",
      "El mercado contiene la respiración.",
      "Quietud antes de la tormenta. O del sol.",
      "Mentes laterales hacen trades laterales.",
      "El consenso es frágil aquí.",
      "El medio es el lugar más difícil para estar.",
      "¿Sin tesis? Sin operación. Está bien.",
      "El aburrimiento es alfa subestimado.",
      "El gráfico descansa. Tú también deberías.",
      "Cincuenta-cincuenta se siente honesto por una vez.",
    ],
    fr: [
      "Pas d'avantage. Reste affûté.",
      "L'équilibre dure rarement.",
      "Rien d'évident. Observe de près.",
      "Émotion plate. Signal utile.",
      "Le marché réfléchit.",
      "L'équilibre est temporaire.",
      "Ni peur ni cupidité. Juste des données.",
      "Le calme entre les tempêtes.",
    ],
    de: [
      "Kein Vorteil. Bleib scharf.",
      "Gleichgewicht hält selten.",
      "Nichts Offensichtliches. Genau beobachten.",
      "Flache Emotion. Nützliches Signal.",
      "Der Markt denkt nach.",
      "Gleichgewicht ist vorübergehend.",
      "Keine Angst. Keine Gier. Nur Daten.",
      "Die Ruhe zwischen den Stürmen.",
    ],
    it: [
      "Nessun vantaggio. Resta attento.",
      "L'equilibrio dura raramente.",
      "Nulla di ovvio. Osserva da vicino.",
      "Emozione piatta. Segnale utile.",
      "Il mercato sta pensando.",
      "L'equilibrio è temporaneo.",
      "Né paura né avidità. Solo dati.",
      "La calma tra le tempeste.",
    ],
    pt: [
      "Sem vantagem. Fique atento.",
      "O equilíbrio raramente dura.",
      "Nada óbvio. Observe de perto.",
      "Emoção plana. Sinal útil.",
      "O mercado está pensando.",
      "O equilíbrio é temporário.",
      "Sem medo. Sem ganância. Só dados.",
      "A calmaria entre tempestades.",
    ],
  },
  "Greed": {
    en: [
      "Confidence is rising.",
      "Momentum feels comfortable.",
      "The crowd is leaning in.",
      "Green attracts stories.",
      "Optimism is growing. So is risk.",
      "Easy gains feel like skill.",
      "The market is smiling. Don't get lazy.",
      "Greed whispers: 'Just a little more.'",
      "Rising tides lift all boats. And all egos.",
      "The trend is friendly. Stay disciplined.",
      "Good vibes. Bad risk management.",
      "FOMO is knocking. Answer carefully.",
      "The crowd is bullish. Where's your stop?",
      "Winners forget about risk. Temporarily.",
      "Sentiment is warm. Don't overheat.",
      "The narrative feels bulletproof. It isn't.",
      "Green days compound. So does overconfidence.",
      "The market rewards conviction. And punishes stubbornness.",
      "Leverage feels safe. It never is.",
      "Profits are real only when realized.",
      "Comfort is the most expensive emotion.",
      "Bull markets make everyone a strategist.",
      "The music is playing. Where's the door?",
      "Growth stories are loudest near the top.",
      "Momentum is a friend that leaves without warning.",
      // Phase 4 additions
      "Green candles hide red flags.",
      "The market is generous. For now.",
      "Conviction or stubbornness? Hard to tell up here.",
      "Everyone's a genius in a bull market. Even you.",
      "The exit strategy matters more than the entry.",
      "Up only works until it doesn't.",
      "Profit-taking never trends on social media.",
      "Euphoria is a gradient, not a switch.",
      "The smartest move might be the boring one.",
      "Gains are not goals. Plans are.",
    ],
    es: [
      "La confianza está subiendo.",
      "El momentum se siente cómodo.",
      "La multitud se está inclinando.",
      "El verde atrae historias.",
      "El optimismo crece. También el riesgo.",
      "Las ganancias fáciles se sienten como habilidad.",
      "El mercado sonríe. No te relajes.",
      "La codicia susurra: 'Solo un poco más.'",
      "La marea sube todos los barcos. Y todos los egos.",
      "La tendencia es amigable. Mantén la disciplina.",
      "Buenas vibras. Mala gestión de riesgo.",
      "El FOMO está llamando. Responde con cuidado.",
      "La multitud es alcista. ¿Dónde está tu stop?",
      "Los ganadores olvidan el riesgo. Temporalmente.",
      "El sentimiento es cálido. No te sobrecalientes.",
      "La narrativa parece invulnerable. No lo es.",
      "Los días verdes se acumulan. También el exceso de confianza.",
      "El mercado premia la convicción. Y castiga la terquedad.",
      "El apalancamiento se siente seguro. Nunca lo es.",
      "Las ganancias son reales solo cuando se realizan.",
      "La comodidad es la emoción más cara.",
      "Los mercados alcistas hacen que todos sean estrategas.",
      "La música suena. ¿Dónde está la puerta?",
      "Las historias de crecimiento son más fuertes cerca del tope.",
      "El momentum es un amigo que se va sin avisar.",
      // Phase 4 additions
      "Las velas verdes esconden banderas rojas.",
      "El mercado es generoso. Por ahora.",
      "¿Convicción o terquedad? Difícil de saber desde aquí arriba.",
      "Todos son genios en un mercado alcista. Incluso tú.",
      "La estrategia de salida importa más que la entrada.",
      "Solo sube funciona hasta que deja de funcionar.",
      "Tomar ganancias nunca es tendencia en redes sociales.",
      "La euforia es un gradiente, no un interruptor.",
      "El movimiento más inteligente podría ser el aburrido.",
      "Las ganancias no son metas. Los planes sí.",
    ],
    fr: [
      "La confiance monte.",
      "Le momentum est confortable.",
      "La foule s'engage.",
      "L'optimisme grandit. Le risque aussi.",
      "Les gains faciles ressemblent à du talent.",
      "La cupidité murmure : 'Encore un peu.'",
      "Le FOMO frappe. Réponds avec prudence.",
      "Le confort est l'émotion la plus chère.",
    ],
    de: [
      "Das Vertrauen steigt.",
      "Das Momentum fühlt sich bequem an.",
      "Die Masse lehnt sich rein.",
      "Optimismus wächst. Risiko auch.",
      "Leichte Gewinne fühlen sich wie Können an.",
      "Gier flüstert: 'Nur noch ein bisschen.'",
      "FOMO klopft an. Antworte vorsichtig.",
      "Komfort ist die teuerste Emotion.",
    ],
    it: [
      "La fiducia sale.",
      "Il momentum è confortevole.",
      "La folla si sta sbilanciando.",
      "L'ottimismo cresce. Anche il rischio.",
      "I guadagni facili sembrano abilità.",
      "L'avidità sussurra: 'Solo un altro po'.'",
      "Il FOMO bussa. Rispondi con attenzione.",
      "Il comfort è l'emozione più cara.",
    ],
    pt: [
      "A confiança está subindo.",
      "O momentum está confortável.",
      "A multidão está se inclinando.",
      "O otimismo cresce. O risco também.",
      "Ganhos fáceis parecem habilidade.",
      "A ganância sussurra: 'Só mais um pouco.'",
      "O FOMO está batendo. Responda com cuidado.",
      "O conforto é a emoção mais cara.",
    ],
  },
  "Extreme Greed": {
    en: [
      "Euphoria is loud. Listen carefully.",
      "Everyone feels like a genius.",
      "Excitement is peaking.",
      "When it feels easy, slow down.",
      "The crowd is all in. Who's left to buy?",
      "Greed has no memory.",
      "Peak confidence. Peak fragility.",
      "The party is crowded. Check the exits.",
      "This time is different. (It never is.)",
      "Maximum conviction. Maximum caution.",
      "Euphoria doesn't ring a bell. It sings a choir.",
      "The market loves you. Don't love it back.",
      "Every asset has a story. Not every story is true.",
      "The herd is running. Where's the cliff?",
      "Gains feel infinite. They aren't.",
      "Risk is invisible when returns are visible.",
      "Overconfidence is the most expensive bias.",
      "The best trades feel terrible. The worst feel amazing.",
      "When taxi drivers give tips, listen to what you hear.",
      "Extreme greed is fear's future self.",
      "The top is a process, not a moment.",
      "Parabolic moves end parabolically.",
      "Everyone has a plan until sentiment flips.",
      "Euphoria is the market's anesthesia.",
      "The loudest optimism hides the deepest risk.",
      // Phase 4 additions
      "The last buyer is already in. Who's next?",
      "Mania feels like clarity. It isn't.",
      "The crowd is dancing on the ceiling.",
      "Unrealized gains are unrealized for a reason.",
      "History doesn't repeat. But it sure does rhyme.",
      "The chart looks perfect. That's the problem.",
      "Everyone agrees. That's rarely bullish.",
      "Dopamine is not a strategy.",
      "The higher you fly, the thinner the air.",
      "Extreme greed is just extreme fear in disguise.",
    ],
    es: [
      "La euforia es ruidosa. Escucha con cuidado.",
      "Todos se sienten genios.",
      "La emoción está en su punto máximo.",
      "Cuando se siente fácil, desacelera.",
      "La multitud está toda dentro. ¿Quién queda para comprar?",
      "La codicia no tiene memoria.",
      "Máxima confianza. Máxima fragilidad.",
      "La fiesta está llena. Revisa las salidas.",
      "Esta vez es diferente. (Nunca lo es.)",
      "Máxima convicción. Máxima cautela.",
      "La euforia no toca una campana. Canta un coro.",
      "El mercado te ama. No lo ames de vuelta.",
      "Cada activo tiene una historia. No todas son ciertas.",
      "El rebaño corre. ¿Dónde está el precipicio?",
      "Las ganancias se sienten infinitas. No lo son.",
      "El riesgo es invisible cuando los retornos son visibles.",
      "El exceso de confianza es el sesgo más caro.",
      "Las mejores operaciones se sienten terribles. Las peores, increíbles.",
      "Cuando los taxistas dan consejos, escucha lo que oyes.",
      "La codicia extrema es el yo futuro del miedo.",
      "El tope es un proceso, no un momento.",
      "Los movimientos parabólicos terminan parabólicamente.",
      "Todos tienen un plan hasta que el sentimiento cambia.",
      "La euforia es la anestesia del mercado.",
      "El optimismo más fuerte esconde el riesgo más profundo.",
      // Phase 4 additions
      "El último comprador ya entró. ¿Quién sigue?",
      "La manía se siente como claridad. No lo es.",
      "La multitud baila en el techo.",
      "Las ganancias no realizadas lo son por una razón.",
      "La historia no se repite. Pero rima.",
      "El gráfico se ve perfecto. Ese es el problema.",
      "Todos están de acuerdo. Eso rara vez es alcista.",
      "La dopamina no es una estrategia.",
      "Cuanto más alto vuelas, más delgado es el aire.",
      "La codicia extrema es solo miedo extremo disfrazado.",
    ],
    fr: [
      "L'euphorie est bruyante. Écoute bien.",
      "Tout le monde se sent génie.",
      "Quand ça semble facile, ralentis.",
      "La foule est all-in. Qui reste pour acheter ?",
      "Confiance maximale. Fragilité maximale.",
      "La fête est bondée. Vérifie les sorties.",
      "Les gains semblent infinis. Ils ne le sont pas.",
      "L'excès de confiance est le biais le plus cher.",
    ],
    de: [
      "Euphorie ist laut. Hör genau hin.",
      "Jeder fühlt sich wie ein Genie.",
      "Wenn es sich leicht anfühlt, bremse.",
      "Die Masse ist all-in. Wer kauft noch?",
      "Maximales Vertrauen. Maximale Fragilität.",
      "Die Party ist voll. Prüfe die Ausgänge.",
      "Gewinne fühlen sich unendlich an. Sind sie nicht.",
      "Übermut ist der teuerste Bias.",
    ],
    it: [
      "L'euforia è rumorosa. Ascolta bene.",
      "Tutti si sentono geni.",
      "Quando sembra facile, rallenta.",
      "La folla è all-in. Chi resta per comprare?",
      "Fiducia massima. Fragilità massima.",
      "La festa è affollata. Controlla le uscite.",
      "I guadagni sembrano infiniti. Non lo sono.",
      "L'eccesso di fiducia è il bias più caro.",
    ],
    pt: [
      "A euforia é barulhenta. Ouça com atenção.",
      "Todo mundo se sente gênio.",
      "Quando parece fácil, desacelere.",
      "A multidão está all-in. Quem sobrou para comprar?",
      "Confiança máxima. Fragilidade máxima.",
      "A festa está lotada. Verifique as saídas.",
      "Os ganhos parecem infinitos. Não são.",
      "Excesso de confiança é o viés mais caro.",
    ],
  },
};

/**
 * Deterministic message selection based on date + bucket + value.
 * Returns the same message for the same inputs throughout a day.
 */
export function getRitualMessage(
  bucket: SentimentBucket,
  value: number,
  locale: string = "en",
  date: Date = new Date(),
  tapIndex: number = 0
): string {
  const pool = messages[bucket]?.[locale] ?? messages[bucket]?.en ?? [];
  if (pool.length === 0) return "";
  const dateStr = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  const hash = simpleHash(`${dateStr}-${bucket}-${value}-${tapIndex}`);
  return pool[Math.abs(hash) % pool.length];
}

function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return hash;
}


